import * as turf from '@turf/turf';

const CIRCLE_STEPS = 64;

const DragCircleMode = {
  onSetup() {
    const polygon = this.newFeature({
      type: 'Feature',
      properties: {
        isCircle: true,
        center: [0, 0],
        radiusKm: 0
      },
      geometry: {
        type: 'Polygon',
        coordinates: [[[0, 0], [0, 0], [0, 0], [0, 0]]]
      }
    });

    this.addFeature(polygon);
    this.clearSelectedFeatures();
    this.updateUIClasses({ mouse: 'add' });
    this.setActionableState({ trash: true });
    this.map.dragPan.disable();

    const canvas = this.map.getCanvas();
    canvas.style.cursor = 'crosshair';
    setTimeout(() => { canvas.style.cursor = 'crosshair'; }, 0);

    return {
      polygon,
      center: null,
      centerSet: false,
      isDragging: false,
      mousedownPoint: null
    };
  },

  onMouseDown(state, e) {
    e.originalEvent.stopPropagation();
    state.mousedownPoint = [e.lngLat.lng, e.lngLat.lat];
  },

  onDrag(state, e) {
    this.map.getCanvas().style.cursor = 'crosshair';

    if (!state.isDragging) {
      state.center = state.mousedownPoint;
      state.centerSet = true;
      state.isDragging = true;
      state.polygon.properties.center = state.center;
      state.polygon.properties.isCircle = true;
    }

    const radiusKm = turf.distance(
      turf.point(state.center),
      turf.point([e.lngLat.lng, e.lngLat.lat]),
      { units: 'kilometers' }
    );

    if (radiusKm < 0.01) return;

    state.polygon.incomingCoords(
      turf.circle(state.center, radiusKm, { steps: CIRCLE_STEPS, units: 'kilometers' }).geometry.coordinates
    );
    state.polygon.properties.radiusKm = radiusKm;
  },

  onMouseUp(state) {
    state.mousedownPoint = null;

    if (state.isDragging) {
      state.isDragging = false;
      if (state.polygon.properties.radiusKm >= 0.1) {
        this._finishCircle(state);
      } else {
        state.centerSet = false;
        state.center = null;
        state.polygon.properties.radiusKm = 0;
        state.polygon.properties.center = null;
      }
      return;
    }

    if (state.centerSet && state.polygon.properties.radiusKm >= 0.1) {
      this._finishCircle(state);
    }
  },

  onClick(state, e) {
    e.originalEvent.stopPropagation();
    state.mousedownPoint = null;

    if (!state.centerSet) {
      state.center = [e.lngLat.lng, e.lngLat.lat];
      state.centerSet = true;
      state.polygon.properties.center = state.center;
      state.polygon.properties.isCircle = true;
      return;
    }

    const radiusKm = turf.distance(
      turf.point(state.center),
      turf.point([e.lngLat.lng, e.lngLat.lat]),
      { units: 'kilometers' }
    );

    if (radiusKm < 0.1) return;

    state.polygon.incomingCoords(
      turf.circle(state.center, radiusKm, { steps: CIRCLE_STEPS, units: 'kilometers' }).geometry.coordinates
    );
    state.polygon.properties.radiusKm = radiusKm;
    this._finishCircle(state);
  },

  onMouseMove(state, e) {
    this.map.getCanvas().style.cursor = 'crosshair';
    if (!state.centerSet || state.isDragging) return;

    const radiusKm = turf.distance(
      turf.point(state.center),
      turf.point([e.lngLat.lng, e.lngLat.lat]),
      { units: 'kilometers' }
    );

    if (radiusKm < 0.01) return;

    state.polygon.incomingCoords(
      turf.circle(state.center, radiusKm, { steps: CIRCLE_STEPS, units: 'kilometers' }).geometry.coordinates
    );
    state.polygon.properties.radiusKm = radiusKm;
  },

  onKeyUp(state, e) {
    if (e.keyCode === 27) {
      this.deleteFeature([state.polygon.id], { silent: true });
      this.changeMode('simple_select');
    }
  },

  onStop(state) {
    this.map.dragPan.enable();
    this.map.getCanvas().style.cursor = '';
    this.updateUIClasses({ mouse: 'none' });
    this.activateUIButton();
    if (this.getFeature(state.polygon.id) === undefined) return;
    if (state.polygon.properties.radiusKm <= 0) {
      this.deleteFeature([state.polygon.id], { silent: true });
    }
  },

  toDisplayFeatures(state, geojson, display) {
    geojson.properties.active = geojson.properties.id === state.polygon.id ? 'true' : 'false';
    if (!state.centerSet) return;
    if (geojson.geometry.coordinates.length > 0 && geojson.geometry.coordinates[0].length > 2) {
      display(geojson);
    }
  },

  _finishCircle(state) {
    this.map._drawJustFinished = true;
    setTimeout(() => { this.map._drawJustFinished = false; }, 200);
    this.map.fire('draw.create', { features: [state.polygon.toGeoJSON()] });
    this.changeMode('simple_select', { featureIds: [state.polygon.id] });
  }
};

export default DragCircleMode;